import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { LogOut, Search, Heart } from "lucide-react";
import { Input } from "@/components/ui/input";
import bidnovaLogo from "@/assets/bidnova-logo.png";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";
import { useMyProfile } from "@/hooks/use-my-profile";
import { resolvePublicSellerName } from "@/lib/public-name";
import { NotificationBell } from "@/components/notification-bell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LanguageSelector } from "@/components/language-selector";
import { CurrencySelector } from "@/components/currency-selector";
import { useTranslation } from "@/i18n";
import { useLocalePreferenceSync } from "@/hooks/use-locale-preference";

import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function SiteHeader() {
  const { user, loading } = useAuth();
  const { isAdminOrModerator } = useRole();
  const { data: profile } = useMyProfile();
  const navigate = useNavigate();
  const { t } = useTranslation("navigation");
  useLocalePreferenceSync();
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    navigate({ to: "/auctions", search: (term ? { q: term } : {}) as never });
  };
  const navLink = "shrink-0 rounded-full px-3 py-1.5 transition-colors duration-200 hover:text-foreground hover:bg-white/5";
  const navActive = { className: "text-foreground bg-primary/15" };
  const searchForm = (cls: string) => (
    <form onSubmit={submitSearch} role="search" className={cls}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t("searchPlaceholder", "Search cards, figures, clothing and more…")}
        aria-label={t("searchPlaceholder", "Search cards, figures, clothing and more…")}
        className="h-10 rounded-full border-white/10 bg-white/[0.05] pl-9 focus-visible:border-primary/60"
      />
    </form>
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/", replace: true });
  };

  const publicName = profile
    ? resolvePublicSellerName(profile)
    : user
      ? t("account", "Account")
      : "";
  const initial = (publicName.trim()[0] ?? "A").toUpperCase();

  return (
    <header
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-background/70 backdrop-blur-xl shadow-[0_8px_30px_-15px_rgba(0,0,0,0.5)]"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 sm:h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-8">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={bidnovaLogo}
            alt="BidNova Auctions"
            width={1600}
            height={544}
            className="h-12 sm:h-[64px] w-auto transition-transform duration-300 group-hover:scale-[1.03]"
          />
          <span className="sr-only">BidNova Auctions</span>
        </Link>

        {searchForm("relative hidden md:block flex-1 max-w-xl")}

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSelector />
          <CurrencySelector className="hidden sm:inline-flex" />
          {loading ? (
            <div className="h-10 w-28 rounded-xl skeleton-shimmer" />
          ) : user ? (
            <>
              <Link to="/watchlist" aria-label={t("watchlist", "Watchlist")} className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-white/5">
                <Heart className="h-4 w-4" />
              </Link>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 rounded-xl border-white/10 bg-white/5 backdrop-blur-xl hover:border-white/20 hover:bg-white/10 h-10 px-3"
                    aria-label={t("accountMenu", "Account menu for {name}", { name: publicName })}
                  >
                    <Avatar className="h-6 w-6">
                      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt="" />}
                      <AvatarFallback className="text-[10px]">{initial}</AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline max-w-[140px] truncate">{publicName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl border-white/10 bg-background/90 backdrop-blur-xl">
                  <DropdownMenuItem asChild><Link to="/dashboard">{t("dashboard", "Dashboard")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/settings/profile">{t("accountProfile", "Account & Profile")}</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/seller">{t("sellerDashboard", "Seller Dashboard")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/my-auctions">{t("myAuctions", "My Auctions")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/my-bids">{t("myBids", "My Bids")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/orders">{t("orders", "Orders")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/order-centre">{t("orderCentre", "Order Centre")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/watchlist">{t("watchlist", "Watchlist")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/want-list">{t("wantList", "Want List")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/collection">{t("collection", "Collection")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/notifications">{t("notifications", "Notifications")}</Link></DropdownMenuItem>
                  {isAdminOrModerator && <DropdownMenuItem asChild><Link to="/admin">{t("admin", "Admin")}</Link></DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                    <LogOut className="h-4 w-4 mr-2" /> {t("signOut", "Sign out")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="h-10 px-4 rounded-xl hover:bg-white/5">
                <Link to="/auth">{t("signIn", "Sign in")}</Link>
              </Button>
              <Button asChild size="sm" className="h-10 px-5 rounded-xl bg-gold-gradient btn-lift font-medium">
                <Link to="/auth" search={{ mode: "signup" } as never}>{t("join", "Join")}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-8 pb-2 md:hidden">
        {searchForm("relative")}
      </div>
      <nav aria-label="Marketplace" className="mx-auto max-w-7xl px-2 sm:px-6">
        <div className="flex items-center gap-1 overflow-x-auto pb-2 text-sm text-muted-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Link to="/" className={navLink} activeProps={navActive} activeOptions={{ exact: true }}>{t("home", "Home")}</Link>
          <Link to="/auctions" className={navLink}>{t("browse", "Browse")}</Link>
          <Link to="/auctions" search={{ category: "cards" } as never} className={navLink}>{t("tradingCards", "Trading Cards")}</Link>
          <Link to="/auctions" search={{ category: "collectibles" } as never} className={navLink}>{t("collectibles", "Collectibles")}</Link>
          <Link to="/auctions" search={{ category: "fashion" } as never} className={navLink}>{t("fashion", "Fashion")}</Link>
          <Link to="/auctions" search={{ category: "cards", subcategory: "sealed" } as never} className={navLink}>{t("sealedProducts", "Sealed Products")}</Link>
          <Link to="/auctions" search={{ category: "cards", subcategory: "accessories" } as never} className={navLink}>{t("accessories", "Accessories")}</Link>
          <span className="mx-1 h-4 w-px shrink-0 bg-white/10" aria-hidden />
          <Link to="/trends" className={navLink} activeProps={navActive}>{t("trends", "Trends")}</Link>
          <Link to="/community" className={navLink} activeProps={navActive}>{t("community", "Community")}</Link>
          <Link to="/sell" className={`${navLink} text-primary font-medium`} activeProps={navActive}>{t("sellAnItem", "Sell an Item")}</Link>
        </div>
      </nav>
    </header>
  );
}
